import { checkExhaustive, checkExists } from 'external/dev_april_corgi+/js/common/asserts';
import { Future, resolvedFuture } from 'external/dev_april_corgi+/js/common/futures';
import { Timer } from 'external/dev_april_corgi+/js/common/timer';
import * as corgi from 'external/dev_april_corgi+/js/corgi';
import { Controller, Response } from 'external/dev_april_corgi+/js/corgi/controller';
import { CorgiEvent, DOM_MOUSE, DOM_POINTER } from 'external/dev_april_corgi+/js/corgi/events';
import { HistoryService } from 'external/dev_april_corgi+/js/corgi/history/history_service';
import { DialogService } from 'external/dev_april_corgi+/js/emu/dialog';
import { ACTION, CHANGED, PRESSED } from 'external/dev_april_corgi+/js/emu/events';
import { MenuEntries } from 'external/dev_april_corgi+/js/emu/menu/menu_controller';
import { MenuClassNames } from 'external/dev_april_corgi+/js/emu/menu/menu_element';
import { MenuHandle, MenuService } from 'external/dev_april_corgi+/js/emu/menu/menu_service';

import { getPointSource } from 'java/org/trailcatalog/models/categories';
import { S2LatLng, S2LatLngRect } from 'java/org/trailcatalog/s2';
import { projectS2LatLng, unprojectS2LatLng } from 'js/map/camera';
import { MAPTERHORN_COPYRIGHT } from 'js/map/common/copyrights';
import { LatLngZoom, Vec2 } from 'js/map/common/types';
import { MAP_MOVED, UNCLAIMED_CLICKED } from 'js/map/events';
import { Layer } from 'js/map/layer';
import { LabelObstacles } from 'js/map/layers/label_placer';
import { SkyboxLayer } from 'js/map/layers/skybox_layer';
import { MapController } from 'js/map/map_controller';
import { ANTARCTIC_CAP, ARCTIC_CAP } from 'js/map/layers/mbtile_layer';
import { PolarCapLayer } from 'js/map/layers/polar_cap_layer';
import { Elevations } from 'js/map/workers/elevation/elevations';
import { LocationIndex } from 'js/map/workers/routing/location_index';
import { Z_BOTTOM } from 'js/map/z';
import { getUnitSystem, setUnitSystem, UnitSystem } from 'js/units/formatters';
import {
  Collection,
  CreateCollectionResponse,
  DeleteAccountResponse,
  DeleteCollectionResponse,
  GetAccountResponse,
  GetCollectionResponse,
  GetCurrentUserResponse,
  GetTimeZoneResponse,
  ListCollectionsResponse,
  SaveResponse,
  SetViewStateResponse,
} from 'trails_lat/proto/data_pb';

import { AccountDialog, ConfirmDeleteAccountDialog } from './account/account_dialog';
import { CollectionSettingsDialog } from './account/collection_settings_dialog';
import { LoginReason } from './account/login_page';
import { LoginPopup } from './account/login_popup';
import { canEdit } from './data/collection_data';
import { requestData } from './data/data';
import { CollectionLayer, HeldObject } from './layers/collection_layer';
import { FeatureLayer } from './layers/feature_layer';
import { LayerCatalog, SKY_LAYER_ID } from './layers/layer_catalog';
import { PointCollectionLayer } from './layers/point_collection_layer';
import {
  FEATURE_CLICKED,
  FEATURE_CONTEXT_MENU,
  FEATURE_EDITED,
  FEATURE_HOVERED,
  FIELD_EDITED,
  FIELD_ICON_CLICKED,
  LayerObject,
  LINE_DRAWN,
  LineSpot,
  MARKER_DRAWN,
  POINT_PLACED,
  POLYGON_DRAWN,
  SKY_PINS_MOVED,
  Tool,
  TOOL_REQUESTED,
} from './model/events';
import { toGeoJson, toGpx } from './model/exporter';
import { Change, FeatureStore } from './model/feature_store';
import {
  EditableFeature,
  EditableLine,
  EditablePolygon,
  EditableShape,
  FeatureData,
  featuresFromProto,
  isShape,
  pointIcon,
  schemaToWrite,
  snapshot,
  toWrite,
} from './model/features';
import { getImageIcon, uploadImage } from './model/images';
import { parseImport } from './model/importer';
import { Point } from './model/points';
import { SaveEntry, SaveQueue } from './model/save_queue';
import { getPositionAt } from './model/tracks';
import {
  adoptImported,
  channelOf,
  getFieldKind,
  getObjectTitle,
  parseCollectionSchema,
  SavedSchema,
  Schema,
  titleField,
  withDefaults,
} from './model/schema';
import {
  fromWallClock,
  isKnownZone,
  readZoneChoice,
  setShownZone,
  writeZoneChoice,
  ZoneChoice,
} from './sky/time_zones';
import { DrawingLayer } from './tools/drawing_layer';
import {
  insertVertex,
  joinLines,
  minVertexCount,
  reverseLine,
  splitLine,
} from './tools/line_edits';
import { LineRefiner } from './tools/line_refiner';
import { MarkerToolLayer } from './tools/marker_tool_layer';
import { haversineMeters, pathLengthMeters, profileSamples } from './tools/measurements';
import { PointToolLayer } from './tools/point_tool_layer';
import {
  ConfirmDeleteDialog,
  DeleteAccountFailedDialog,
  ImportFailedDialog,
  SaveFailedDialog,
} from './ui/dialogs';
import { ExportDialog } from './ui/export_dialog';
import {
  CollectionStatus,
  FeatureListState,
  InspectedObject,
  topLevelFolderIds,
} from './ui/feature_list';
import { IconPickerDialog } from './ui/icon_picker';
import {
  applyLayerSettings,
  ControlValues,
  diffLayerSettings,
  findLayerNode,
  layerDefaultsOf,
  LayerEntry,
  layerEntries,
  LayerNode,
  LayerSetting,
  LayerSettings,
  LayerTreeState,
  updateLayerNode,
} from './ui/layer_tree';
import { GradeLimitState, MeasureState } from './ui/measure_panel';
import { MENU_CLASSES } from './ui/menubar';
import { editMenu, fileMenu, HELP_MENU, userMenu, viewMenu } from './ui/menus';
import { Choice, choiceItem, PICK_MENU_CLASSES, readingItem } from './ui/pick_menu';
import { LineProfileState } from './ui/profile_chart';
import { LineProfiler, MeasureProfiler } from './ui/profiles';
import { SearchHit, SearchQuery, SearchState } from './ui/search_box';
import { STEP_MS, TimelineState } from './ui/timeline';
import { ToastService } from './ui/toasts';
import {
  CollectionSchema,
  DEFAULT_COLOR,
  DEFAULT_ICON,
  getField,
  getLineKind,
  pointLook,
} from './workers/collection_schema';

const MINUTE_MS = 60 * 1000;
const PLAY_INTERVAL_MS = 100;
const FORECAST_PLAY_TICKS = 5;
const UPLOADS_IN_FLIGHT = 2;
// Flattens the points and line and polygon vertices of some features into lat,lng pairs in
// degrees.
function featureLatLngDegrees(features: Iterable<EditableFeature>): number[] {
  const latLngDegrees = [];
  for (const feature of features) {
    if (feature.kind === 'point') {
      latLngDegrees.push(feature.latE7 / 1e7, feature.lngE7 / 1e7);
    } else if (isShape(feature)) {
      for (let i = 0; i < feature.latLngE7.length; i += 2) {
        latLngDegrees.push(feature.latLngE7[i] / 1e7, feature.latLngE7[i + 1] / 1e7);
      }
    }
  }
  return latLngDegrees;
}

// Join with and the elevation profile take route lines, not marker strokes
function isRouteLine(feature: EditableFeature|undefined): feature is EditableLine {
  return feature?.kind === 'line' && getLineKind(feature.data) === 'line';
}

// Something a click landed on
type Pick =
    {kind: 'feature'; feature: EditableFeature}
    |{kind: 'object'; entry: LayerEntry; layer: CollectionLayer; object: HeldObject}
    |{kind: 'point'; entry: LayerEntry; layer: PointCollectionLayer; point: Point};

export interface Args {
  // The id of the open collection or none
  collection: string|undefined;
}

export interface State {
  collection: Collection|undefined;
  collectionStatus: CollectionStatus;
  features: FeatureListState;
  // Set while files the collection can import are dragged over the viewer
  fileDragged: boolean;
  // The map layer object open in the dialog, when no feature is selected
  inspected: InspectedObject|undefined;
  // The placed part of the area under construction, as interleaved lat then lng degrees
  drawnArea: Float64Array|undefined;
  // How far the line, polygon, or measurement under construction reaches, shown beside the
  // pointer. In pixels from the viewer's top left.
  drawnLength: {x: number; y: number; meters: number}|undefined;
  // The line Join with started from, which the next line clicked joins
  joining: string|undefined;
  layers: LayerTreeState;
  // The profile of the selected feature or the inspected object, when that is a line
  lineProfile: LineProfileState|undefined;
  measure: MeasureState;
  // What the terrain tool routes within
  grades: GradeLimitState;
  search: SearchState;
  self: Future<GetCurrentUserResponse>;
  time: TimelineState;
  tool: Tool;
}

type Deps = typeof ViewerController.deps;

export class ViewerController extends Controller<Args, Deps, HTMLElement, State> {

  static deps() {
    return {
      controllers: {
        map: MapController,
      },
      services: {
        dialog: DialogService,
        history: HistoryService,
        menu: MenuService,
        toast: ToastService,
      },
    };
  }

  private readonly mapController: MapController;
  private readonly dialog: DialogService;
  private readonly history: HistoryService;
  private readonly menu: MenuService;
  private readonly toast: ToastService;
  private readonly featureLayer: FeatureLayer;
  private readonly lineLayer: DrawingLayer;
  private readonly polygonLayer: DrawingLayer;
  private readonly measureLayer: DrawingLayer;
  private readonly terrainLayer: DrawingLayer;
  private readonly pointLayer: PointToolLayer;
  private readonly markerLayer: MarkerToolLayer;
  private readonly polarCapLayer: PolarCapLayer;
  private readonly skyboxLayer: SkyboxLayer;
  private readonly catalog: LayerCatalog;
  private readonly clock: Timer;
  private readonly player: Timer;
  private playTicks: number;
  // The layer entries on the map, so that setMapLayers can tell which were just turned on
  private shownIds: Set<string>;
  private readonly store: FeatureStore;
  private readonly elevations: Elevations;
  private readonly measureProfiler: MeasureProfiler;
  private readonly lineProfiler: LineProfiler;
  private selected: string|undefined;
  // Kept for centering on, which needs the geometry the dialog's state leaves out, along with how
  // to stop its layer drawing it as selected. Loads for it check that it's still the same record.
  private inspected: {object: LayerObject; unmark: () => void}|undefined;
  // Moves with every click, so that a slow query for an earlier one is dropped
  private pickGeneration: number;
  // The pick menu and the camera when it opened. The map reports a move whenever it settles, even
  // when the camera stayed put, and only a real move takes the menu away from its click.
  private picked: {camera: LatLngZoom; menu: MenuHandle}|undefined;
  private zoneChoice: ZoneChoice;
  // Moves with every zone lookup, so that a slow answer for where the map was is dropped
  private zoneGeneration: number;
  private readonly searchQuery: SearchQuery;
  // Moves with every collection opened or created, so that a slow load of an earlier one is dropped
  private collectionGeneration: number;
  // The collection id from the URL that the store holds or is loading, so that a render with the
  // same route doesn't reload it
  private routedCollection: string|undefined;
  // The feature being dragged in the list
  private draggedFeature: string|undefined;
  // Where the pointer last was, in pixels from the viewer's top left
  private pointer: [number, number]|undefined;
  // Settles once the login popup closes and the user is fetched, so concurrent callers share it
  private login: Promise<void>|undefined;
  // Settles once a new collection is created, so concurrent saves and uploads share one
  private creatingCollection: Promise<string>|undefined;
  private readonly loginPopup: LoginPopup;
  private readonly saves: SaveQueue;
  // What the layer tree starts as, which saved layer settings are relative to
  private readonly layerDefaults: Map<string, LayerSetting>;
  // Moves with every layer toggle or control change, so that a collection's saved layers don't
  // undo one made while it loaded
  private layerEdits: number;
  // Serialized so that an older view state cannot land after a newer one
  private viewStateWrites: Promise<void>;
  // The menubar item whose menu is open or loading. A File menu still waiting on ListCollections
  // when another menu takes over sees it change and stays closed.
  private menuTrigger: Element|undefined;
  // The open menu, whether the menubar's, a context menu, or a click's pick menu
  private openedMenu: MenuHandle|undefined;
  // A failure the dialog is already up for stays quiet, or else every retry that fails for the
  // same reason stacks another one on the page.
  private warnedUnsaved: boolean;

  constructor(response: Response<ViewerController>) {
    super(response);
    this.mapController = response.deps.controllers.map;
    this.dialog = response.deps.services.dialog;
    this.history = response.deps.services.history;
    this.menu = response.deps.services.menu;
    this.toast = response.deps.services.toast;
    this.saves = new SaveQueue(entries => this.save(entries), e => {
      console.error(e);
      this.warnUnsaved();
    });
    this.store =
        new FeatureStore(this.saves, /* readOnly= */ response.args.collection !== undefined);
    this.viewStateWrites = Promise.resolve();
    this.elevations = new Elevations();
    this.measureProfiler =
        new MeasureProfiler(
            this.elevations,
            () => toDegrees(this.measuringLayer().placedPoints()),
            () => this.state.measure,
            measure => {
              this.updateState({...this.state, measure});
            });
    this.lineProfiler =
        new LineProfiler(
            this.elevations,
            () => this.state.lineProfile,
            lineProfile => {
              this.updateState({...this.state, lineProfile});
            });
    this.selected = undefined;
    this.inspected = undefined;
    this.pickGeneration = 0;
    this.picked = undefined;
    this.zoneChoice = readZoneChoice();
    this.zoneGeneration = 0;
    this.searchQuery =
        new SearchQuery(
            () => {
              const center = this.mapController.camera.center;
              const corner = this.mapController.viewportBounds.lo();
              const radiusMeters =
                  haversineMeters(
                      center.latDegrees(),
                      center.lngDegrees(),
                      corner.latDegrees(),
                      corner.lngDegrees());
              return {center, radiusMeters};
            },
            () => this.state.search,
            search => {
              this.updateState({...this.state, search});
            });
    this.collectionGeneration = 0;
    this.routedCollection = response.args.collection;
    this.draggedFeature = undefined;
    this.pointer = undefined;
    this.store.listen(() => {
      this.refreshFeatureList();
    });
    this.warnedUnsaved = false;
    this.menuTrigger = undefined;
    this.openedMenu = undefined;

    this.loginPopup = new LoginPopup(response => {
      response.then(r => {
        this.signedIn(r);
      }, e => {
        console.error(e);
      });
    });
    this.registerDisposable(this.loginPopup);

    // One index for every layer, so that the drawing tools snap onto and route across drawn
    // lines and paths alike.
    const locations = new LocationIndex();
    const labelObstacles = new LabelObstacles();
    // One cache, so that measuring a line just drawn doesn't fetch its lines again
    const refiner = new LineRefiner();
    const camera = this.mapController.camera;
    const renderer = this.mapController.renderer;
    // Line profiles and new vertices sample their elevations from Mapterhorn
    this.featureLayer =
        new FeatureLayer(
            [MAPTERHORN_COPYRIGHT],
            this.store,
            locations,
            refiner,
            camera,
            renderer,
            labelObstacles);
    this.registerDisposable(this.featureLayer);
    this.featureLayer.setHidden(this.state.features.hidden);
    this.lineLayer =
        new DrawingLayer(
            'line',
            locations,
            refiner,
            camera,
            renderer,
            () => {
              this.measureChanged();
              this.drawnLengthChanged();
            },
            [MAPTERHORN_COPYRIGHT]);
    this.registerDisposable(this.lineLayer);
    this.polygonLayer =
        new DrawingLayer(
            'polygon',
            locations,
            refiner,
            camera,
            renderer,
            () => {
              this.drawnAreaChanged();
              this.drawnLengthChanged();
            });
    this.registerDisposable(this.polygonLayer);
    this.measureLayer =
        new DrawingLayer(
            'measure',
            locations,
            refiner,
            camera,
            renderer,
            () => {
              this.measureChanged();
              this.drawnLengthChanged();
            },
            [MAPTERHORN_COPYRIGHT]);
    this.registerDisposable(this.measureLayer);
    // A line mode, because finishing a route is how to keep it
    this.terrainLayer =
        new DrawingLayer(
            'line',
            locations,
            refiner,
            camera,
            renderer,
            () => {
              this.measureChanged();
              this.drawnLengthChanged();
            },
            [MAPTERHORN_COPYRIGHT],
            (from, to) => {
              const {climbPercent, descentPercent} = this.state.grades;
              return this.elevations.route(
                  from, to, {climb: climbPercent / 100, descent: descentPercent / 100});
            },
            () => this.store.isReadOnly());
    this.registerDisposable(this.terrainLayer);
    this.pointLayer = new PointToolLayer();
    this.registerDisposable(this.pointLayer);
    this.markerLayer = new MarkerToolLayer(camera, renderer);
    this.registerDisposable(this.markerLayer);

    this.registerListener(window, 'keydown', e => {
      this.keyPressed(e);
    });
    // On the root, which hears a move before the map's own listener on document does, so the
    // drawing's change sees where the pointer is now.
    this.registerListener(this.root, 'pointermove', e => {
      const bounds = this.root.getBoundingClientRect();
      this.pointer = [e.clientX - bounds.left, e.clientY - bounds.top];
    });

    this.polarCapLayer =
        new PolarCapLayer(ARCTIC_CAP, ANTARCTIC_CAP, this.mapController.renderer);
    this.registerDisposable(this.polarCapLayer);
    this.skyboxLayer = new SkyboxLayer(Z_BOTTOM, this.mapController.renderer);
    this.registerDisposable(this.skyboxLayer);

    this.shownIds = new Set();
    this.catalog =
        new LayerCatalog(
            locations,
            labelObstacles,
            camera,
            this.elevations,
            renderer,
            () => this.state.layers.nodes,
            () => {
              this.updateTimeline({});
            });
    this.registerDisposable(this.catalog);
    const nodes = this.catalog.nodes;
    this.layerDefaults = layerDefaultsOf(nodes);
    this.layerEdits = 0;
    this.updateState({
      ...this.state,
      layers: {nodes, expanded: new Set(['osm', 'satellite', 'topography', 'us'])},
    });
    this.updateTimeline({now: Date.now()});
    this.updateZone(camera.center);
    this.catalog.start();
    this.playTicks = 0;
    this.player = new Timer(PLAY_INTERVAL_MS, () => {
      this.advanceTimeline();
    });
    this.registerDisposable(this.player);
    this.clock = new Timer(MINUTE_MS, () => {
      if (this.state.time.at === undefined) {
        this.catalog.setTime(undefined);
      }
      this.updateTimeline({now: Date.now()});
    });
    this.registerDisposable(this.clock);
    this.clock.start();
    this.setMapLayers(nodes);

    const collection = response.args.collection;
    // The server render already answered with the outline's failure, and a reload is the retry.
    if (collection && this.state.collectionStatus !== 'failed') {
      this.loadCollection(collection);
    }
  }

  // Opens whatever collection the URL names, including after the back and forward buttons.
  updateArgs(newArgs: Args): void {
    if (newArgs.collection === this.routedCollection) {
      return;
    }

    const id = newArgs.collection;
    this.routedCollection = id;
    this.collectionGeneration += 1;
    this.saves.clear();
    this.select(undefined);
    this.setHidden([]);
    // Empties the list while the collection loads, or else the last one's rows are left to click.
    // Loading replaces the store, so nothing is editable until it lands.
    this.resetStore([], id !== undefined);
    this.updateState({
      ...this.state,
      collection: this.state.collection?.id === id ? this.state.collection : undefined,
      collectionStatus: 'loaded',
      features: {...this.state.features, expanded: new Set()},
    });
    if (id) {
      this.loadCollection(id);
    }
  }

  // Selects the new line, like onPointPlaced, so that the panel is open to name it.
  onLineDrawn(e: CorgiEvent<typeof LINE_DRAWN>): void {
    this.addLine(e.detail.latLngE7, {});
  }

  // Selects the new polygon, like onPointPlaced, so that the panel is open to name it.
  onPolygonDrawn(e: CorgiEvent<typeof POLYGON_DRAWN>): void {
    const polygon: EditablePolygon = {
      kind: 'polygon',
      id: crypto.randomUUID(),
      version: 0n,
      data: {},
      latLngE7: e.detail.latLngE7,
    };
    this.addFeature(polygon);
  }

  // Selects the new stroke, like onPointPlaced, so that the panel is open to name it.
  onMarkerDrawn(e: CorgiEvent<typeof MARKER_DRAWN>): void {
    this.addLine(e.detail.latLngE7, {marker_zoom: e.detail.zoom});
  }

  // Selects the new point so that the panel is open to name it, and stays in the tool so that
  // several can go down in a row.
  onPointPlaced(e: CorgiEvent<typeof POINT_PLACED>): void {
    const point: EditableFeature = {
      kind: 'point',
      id: crypto.randomUUID(),
      version: 0n,
      data: {},
      latE7: e.detail.latE7,
      lngE7: e.detail.lngE7,
      elevationCentimeters: undefined,
    };
    this.addFeature(point);
  }

  onToolRequested(e: CorgiEvent<typeof TOOL_REQUESTED>): void {
    this.setTool(e.detail.tool);
  }

  onFeatureClicked(e: CorgiEvent<typeof FEATURE_CLICKED>): void {
    this.clickFeature(e.detail.id);
  }

  // Selects or opens the one thing a click landed on, or else offers what it landed on beside what
  // the shown layers read there. A click on nothing clears the selection.
  onUnclaimedClicked(e: CorgiEvent<typeof UNCLAIMED_CLICKED>): void {
    if (this.state.tool !== 'pointer') {
      return;
    }

    const {point, px} = e.detail;
    this.pickGeneration += 1;
    const generation = this.pickGeneration;
    const joining = this.state.joining;
    // Join with waits on another line, so nothing else is worth offering.
    const shown = joining === undefined ? this.getShownEntries(this.state.layers.nodes) : [];
    const readings: Array<{name: string; value: string}> = [];
    for (const {readout} of shown) {
      const value = readout?.read(point);
      if (readout && value !== undefined) {
        readings.push({name: readout.name, value});
      }
    }
    for (const entry of shown) {
      readings.push(...entry.readings?.(point) ?? []);
    }
    const features: Pick[] =
        this.featureLayer.featuresAt(projectS2LatLng(point))
            .filter(f => joining === undefined || (isRouteLine(f) && f.id !== joining))
            .map(feature => ({kind: 'feature', feature}));
    const others = shown.map(entry => this.picksIn(entry, point));
    Promise.all(others).then(found => {
      if (generation !== this.pickGeneration) {
        return;
      }

      const picks = [...features, ...found.flat()];
      if (picks.length === 0) {
        this.clickFeature(undefined);
      } else if (picks.length === 1 && readings.length === 0) {
        this.openPick(picks[0]);
      }
      // Opening one thing beside a menu of readings reads as two answers to one click, so the
      // person chooses whenever the menu has more than one row.
      const choices = picks.length + readings.length > 1 ? picks : [];
      if (readings.length === 0 && choices.length === 0) {
        return;
      }

      const items: MenuEntries = choices.map(pick =>
          choiceItem(
              this.choiceOf(pick),
              () => {
                this.openPick(pick);
              },
              active => {
                this.highlightPick(pick, active);
              }));
      if (choices.length > 0 && readings.length > 0) {
        items.push({kind: 'divider'});
      }
      items.push(...readings.map(readingItem));
      this.picked = {
        camera: this.mapController.cameraLlz,
        menu: this.openContextMenu(items, px, PICK_MENU_CLASSES),
      };
    });
  }

  private picksIn(entry: LayerEntry, point: S2LatLng): Promise<Pick[]> {
    const layer = entry.layer;
    if (layer instanceof CollectionLayer) {
      return layer.objectsAt(point).then(objects =>
          objects.map(object => ({kind: 'object', entry, layer, object})));
    } else if (layer instanceof PointCollectionLayer) {
      return Promise.resolve(
          layer.pointsAt(point).map(value => ({kind: 'point', entry, layer, point: value})));
    } else {
      return Promise.resolve([]);
    }
  }

  private openPick(pick: Pick): void {
    if (pick.kind === 'feature') {
      this.clickFeature(pick.feature.id);
      return;
    }

    if (pick.kind === 'object') {
      const {entry, layer, object} = pick;
      this.openObject(entry, object, () => {
        layer.selectObject(undefined);
      });
      layer.selectObject(object);
    } else if (pick.kind === 'point') {
      const {entry, layer, point} = pick;
      this.openObject(entry, {kind: 'point', value: point}, () => {
        layer.selectPoint(undefined);
      });
      layer.selectPoint(point);
    } else {
      checkExhaustive(pick);
    }
  }

  private highlightPick(pick: Pick, on: boolean): void {
    if (pick.kind === 'feature') {
      this.setHovered(on ? pick.feature.id : undefined);
    } else if (pick.kind === 'object') {
      pick.layer.highlightObject(on ? pick.object : undefined);
    } else if (pick.kind === 'point') {
      pick.layer.highlightPoint(on ? pick.point : undefined);
    } else {
      checkExhaustive(pick);
    }
  }

  private choiceOf(pick: Pick): Choice {
    if (pick.kind === 'feature') {
      return {kind: 'feature', feature: pick.feature, schema: this.store.schema()};
    }

    const object: LayerObject =
        pick.kind === 'object' ? pick.object : {kind: 'point', value: pick.point};
    const schema = getPickSchema(pick.entry);
    return {
      kind: 'object',
      shape: object.kind,
      title: schema && getObjectTitle(schema, {kind: object.kind, data: object.value.data}),
      source: pick.entry.name,
    };
  }

  // Joins the line clicked while Join with is waiting on one. Anything else cancels the join and
  // selects as usual.
  private clickFeature(id: string|undefined): void {
    const from = this.state.joining;
    const a = from !== undefined ? this.store.get(from) : undefined;
    const b = id !== undefined ? this.store.get(id) : undefined;
    if (isRouteLine(a) && isRouteLine(b) && a.id !== b.id) {
      this.store.apply([
        {id: a.id, before: snapshot(a), after: joinLines(a, b)},
        {id: b.id, before: snapshot(b), after: undefined},
      ]);
      id = a.id;
    }

    this.reveal(id);
  }

  // Opens the folders above what the map selected and scrolls to it, so that the list shows it.
  private reveal(id: string|undefined): void {
    this.expand(this.ancestorsOf(id));
    this.select(id);
    if (id !== undefined) {
      // After the render that puts the row in the list
      requestAnimationFrame(() => {
        this.root.querySelector(`div[data-id="${id}"]`)?.scrollIntoView({block: 'nearest'});
      });
    }
  }

  private openObject(entry: LayerEntry, object: LayerObject, unmark: () => void): void {
    this.select(undefined);
    this.inspected = {object, unmark};
    const data = object.value.data;
    const schema = checkExists(getPickSchema(entry));
    const title = 'layers' in schema ? undefined : schema.title;
    const titleValue = title && data[title.key];
    const osm =
        typeof titleValue !== 'number' ? undefined
            : title?.kind === 'osm_point' ? osmElementOf(titleValue)
            : title?.kind === 'osm_way' ? {type: 'way' as const, id: titleValue}
            : undefined;
    const station = entry.weatherStation ? data[entry.weatherStation.id] : undefined;
    const reports = entry.weatherStation ? String(data[entry.weatherStation.reports] ?? '') : '';
    const weather =
        typeof station === 'string'
            ? {
              station,
              metar: reports.includes('METAR'),
              taf: reports.includes('TAF'),
            }
            : undefined;
    const common = {data, schema, osm, weather};
    if (object.kind === 'line') {
      const path = toDegrees(object.value.points);
      this.updateState({
        ...this.state,
        inspected: {
          ...common,
          kind: 'line',
          lengthMeters: pathLengthMeters(path),
        },
      });
      this.lineProfiler.forObject(path);
    } else if (object.kind === 'point') {
      const ll = unprojectS2LatLng(object.value.position[0], object.value.position[1]);
      this.updateState({
        ...this.state,
        inspected: {
          ...common,
          kind: 'point',
          latDegrees: ll.latDegrees(),
          lngDegrees: ll.lngDegrees(),
        },
      });
    } else {
      this.updateState({...this.state, inspected: {...common, kind: 'polygon'}});
    }
  }

  onFeatureEdited(e: CorgiEvent<typeof FEATURE_EDITED>): void {
    const {before, after, vertex} = e.detail;
    const changes = [{id: after.id, before, after}];
    if (vertex === undefined) {
      this.store.apply(changes);
    } else {
      this.applyPlacingVertices(changes, [{id: after.id, index: vertex}]);
    }
  }

  onFeatureContextMenu(e: CorgiEvent<typeof FEATURE_CONTEXT_MENU>): void {
    const {id, spot, px} = e.detail;
    this.reveal(id);
    const feature = id !== undefined ? this.store.get(id) : undefined;
    const editable = feature !== undefined && !this.store.isReadOnly();
    const items: MenuEntries = [];
    if (editable && feature.kind === 'line' && spot) {
      const count = feature.latLngE7.length / 2;
      items.push({
        kind: 'menu_item',
        label: 'Split here',
        disabled: spot.kind === 'vertex' && (spot.index === 0 || spot.index === count - 1),
        action: () => {
          this.splitLineAt(feature.id, spot);
        },
      });
      items.push({
        kind: 'menu_item',
        label: 'Reverse direction',
        action: () => {
          this.editShape(feature.id, reverseLine);
        },
      });
      if (isRouteLine(feature)) {
        items.push({
          kind: 'menu_item',
          label: 'Join with…',
          disabled: !this.store.lines().some(l => isRouteLine(l) && l.id !== feature.id),
          action: () => {
            this.updateState({...this.state, joining: feature.id});
          },
        });
      }
    }
    if (editable && isShape(feature) && spot?.kind === 'vertex' && spot.handle) {
      items.push({
        kind: 'menu_item',
        label: 'Delete vertex',
        disabled: feature.latLngE7.length / 2 <= minVertexCount(feature),
        action: () => {
          this.featureLayer.deleteVertex(spot.index, this.mapController);
        },
      });
    }
    if (editable) {
      items.push({
        kind: 'menu_item',
        label: 'Duplicate',
        action: () => {
          this.duplicateFeature(feature.id);
        },
      });
    }
    const [latE7, lngE7] = coordinatesOf(feature, spot, e.detail.latE7, e.detail.lngE7);
    items.push({
      kind: 'menu_item',
      label: 'Copy coordinates',
      action: () => {
        const text = `${(latE7 / 1e7).toFixed(6)}, ${(lngE7 / 1e7).toFixed(6)}`;
        navigator.clipboard.writeText(text).catch(e => {
          console.error(e);
        });
      },
    });
    if (editable) {
      items.push({kind: 'divider'});
      items.push({
        kind: 'menu_item',
        label: 'Delete',
        action: () => {
          this.deleteClicked();
        },
      });
    }

    this.openContextMenu(items, px);
  }

  onFeatureHovered(e: CorgiEvent<typeof FEATURE_HOVERED>): void {
    this.setHovered(e.detail.id);
  }

  featureClicked(e: CorgiEvent<typeof DOM_MOUSE>): void {
    this.clickFeature(checkExists(e.actionElement.data('id')).string());
  }

  featureDragStarted(e: CorgiEvent<typeof DOM_MOUSE>): void {
    const id = checkExists(e.actionElement.data('id')).string();
    this.draggedFeature = id;
    const transfer = (e.detail as DragEvent).dataTransfer;
    if (transfer) {
      // Firefox refuses to start a drag that carries no data.
      transfer.setData('text/plain', id);
      transfer.effectAllowed = 'move';
    }
  }

  // Corgi stops a DOM event at the first handler, so the feature tree passes file drags on to the
  // viewer's handlers or else files dropped on it never import.
  featureDraggedOver(e: CorgiEvent<typeof DOM_MOUSE>): void {
    this.dragOver(e, this.dropFolderFor(e));
  }

  featureTreeDraggedOver(e: CorgiEvent<typeof DOM_MOUSE>): void {
    this.dragOver(e, undefined);
  }

  // Clears the target once the drag leaves the tree. Moving between rows leaves one row for
  // another inside it, which keeps the target.
  featureTreeDragLeft(e: CorgiEvent<typeof DOM_MOUSE>): void {
    this.filesDragLeft(e);
    const related = e.detail.relatedTarget;
    if (!(related instanceof Node) || !e.actionElement.element().contains(related)) {
      this.setDropTarget(undefined);
    }
  }

  featureDropped(e: CorgiEvent<typeof DOM_MOUSE>): void {
    this.drop(e, this.dropFolderFor(e));
  }

  featureTreeDropped(e: CorgiEvent<typeof DOM_MOUSE>): void {
    this.drop(e, undefined);
  }

  // Covers the whole viewer, so a drop anywhere imports instead of opening the file in the tab. A
  // photo dropped off the map goes in the middle of the map.
  filesDraggedOver(e: CorgiEvent<typeof DOM_MOUSE>): void {
    const transfer = (e.detail as DragEvent).dataTransfer;
    if (transfer && isFileDrag(e) && !this.store.isReadOnly()) {
      e.detail.preventDefault();
      transfer.dropEffect = 'copy';
      this.setFileDragged(true);
    }
  }

  // Moving between the viewer's children leaves one for another inside it, which keeps the drag.
  filesDragLeft(e: CorgiEvent<typeof DOM_MOUSE>): void {
    const related = e.detail.relatedTarget;
    if (!(related instanceof Node) || !this.root.contains(related)) {
      this.setFileDragged(false);
    }
  }

  filesDropped(e: CorgiEvent<typeof DOM_MOUSE>): void {
    this.setFileDragged(false);
    const transfer = (e.detail as DragEvent).dataTransfer;
    if (!transfer || !isFileDrag(e) || this.store.isReadOnly()) {
      return;
    }
    e.detail.preventDefault();
    this.importFiles(
        Array.from(transfer.files), this.mapController.getLatLngUnder(e.detail as DragEvent));
  }

  featureDragEnded(): void {
    this.draggedFeature = undefined;
    this.setDropTarget(undefined);
  }

  folderToggled(e: CorgiEvent<typeof DOM_MOUSE>): void {
    const id = checkExists(e.actionElement.data('id')).string();
    const expanded = new Set(this.state.features.expanded);
    if (!expanded.delete(id)) {
      expanded.add(id);
    }
    this.updateState({...this.state, features: {...this.state.features, expanded}});
  }

  featureRowEntered(e: CorgiEvent<typeof DOM_POINTER>): void {
    this.setHovered(checkExists(e.actionElement.data('id')).string());
  }

  featureRowLeft(): void {
    this.setHovered(undefined);
  }

  toolPanelClosed(): void {
    this.setTool('pointer');
  }

  // Keeps the tool, like finishing on the last vertex does.
  finishDrawingClicked(): void {
    this.drawingLayer()?.save(this.mapController, () => {});
  }

  undoPointClicked(): void {
    this.drawingLayer()?.popVertex();
  }

  maxClimbChanged(e: CorgiEvent<typeof DOM_MOUSE>): void {
    this.setGrade(e, 'climbPercent');
  }

  maxDescentChanged(e: CorgiEvent<typeof DOM_MOUSE>): void {
    this.setGrade(e, 'descentPercent');
  }

  profileHovered(e: CorgiEvent<typeof DOM_POINTER>): void {
    const line = checkExists(e.actionElement.data('source')).string() === 'line';
    const samples = line ? this.lineProfiler.samples : this.measureProfiler.samples;
    const shown = line ? this.state.lineProfile : this.state.measure.profile;
    if (!samples || samples.distanceMeters.length === 0 || shown?.status !== 'loaded') {
      return;
    }

    // The chart scales its viewBox to its width, so the pointer's share of the plot is its share
    // of the distance.
    const svg = e.actionElement.element();
    const bounds = svg.getBoundingClientRect();
    const width = checkExists(e.actionElement.data('width')).number();
    const left = checkExists(e.actionElement.data('left')).number() * bounds.width / width;
    const right = checkExists(e.actionElement.data('right')).number() * bounds.width / width;
    const fraction =
        Math.min(1, Math.max(0, (e.detail.clientX - bounds.left - left) / (right - left)));
    const distances = samples.distanceMeters;
    const target = fraction * distances[distances.length - 1];
    let index = 0;
    while (index + 1 < distances.length && distances[index + 1] <= target) {
      index += 1;
    }
    const ll =
        S2LatLng.fromDegrees(
            samples.latLngDegrees[2 * index], samples.latLngDegrees[2 * index + 1]);
    const hovered = {...shown, hovered: index};
    (line ? this.featureLayer : this.measuringLayer()).setMarker(projectS2LatLng(ll));
    this.updateState(
        line
            ? {...this.state, lineProfile: hovered}
            : {...this.state, measure: {...this.state.measure, profile: hovered}});
  }

  profileLeft(): void {
    this.lineLayer.setMarker(undefined);
    this.measureLayer.setMarker(undefined);
    this.terrainLayer.setMarker(undefined);
    this.featureLayer.setMarker(undefined);
    this.updateState({
      ...this.state,
      lineProfile: withoutHover(this.state.lineProfile),
      measure: {...this.state.measure, profile: withoutHover(this.state.measure.profile)},
    });
  }

  // Switches tools only once the line lands, because switching clears a drawing still waiting on
  // its routes.
  saveMeasurementClicked(): void {
    this.measuringLayer().save(this.mapController, () => {
      this.setTool('pointer');
    });
  }

  visibilityToggled(e: CorgiEvent<typeof DOM_MOUSE>): void {
    const id = checkExists(e.actionElement.data('id')).string();
    const hidden = new Set(this.state.features.hidden);
    if (!hidden.delete(id)) {
      hidden.add(id);
    }
    this.setHidden(hidden);
    this.saveViewState();
  }

  newFolderClicked(): void {
    this.addFeature({kind: 'folder', id: crypto.randomUUID(), version: 0n, data: {}});
  }

  nameChanged(e: CorgiEvent<typeof CHANGED>): void {
    const name = e.detail.value;
    this.editSelectedData(data => ({...data, name: name || undefined}), 'name');
  }

  fieldEdited(e: CorgiEvent<typeof FIELD_EDITED>): void {
    this.editField(e.detail.field.id, e.detail.value);
  }

  // A folder centers on everything inside it.
  centerClicked(): void {
    const selected = this.selected !== undefined ? this.store.get(this.selected) : undefined;
    const features =
        !selected
            ? []
            : selected.kind === 'folder' ? this.store.descendants(selected.id) : [selected];
    const latLngDegrees = featureLatLngDegrees(features);
    const inspected = this.inspected?.object;
    if (inspected?.kind === 'polygon') {
      const bound = inspected.value.bound;
      latLngDegrees.push(bound.low[0], bound.low[1], bound.high[0], bound.high[1]);
    } else if (inspected?.kind === 'point') {
      const ll = unprojectS2LatLng(inspected.value.position[0], inspected.value.position[1]);
      latLngDegrees.push(ll.latDegrees(), ll.lngDegrees());
    } else if (inspected?.kind === 'line') {
      latLngDegrees.push(...toDegrees(inspected.value.points));
    }
    this.fitCamera(latLngDegrees);
  }

  // Moves the camera to show every point in a flat lat,lng array, or nowhere when it's empty.
  private fitCamera(latLngDegrees: number[]): void {
    let bound = S2LatLngRect.empty();
    for (let i = 0; i < latLngDegrees.length; i += 2) {
      bound = bound.addLatLng(S2LatLng.fromDegrees(latLngDegrees[i], latLngDegrees[i + 1]));
    }
    if (bound.isEmpty()) {
      return;
    }

    // A low longitude above the high one crosses the antimeridian, which setCamera handles. It
    // tells a rect from a center by the brand, so the brand has to exist at runtime.
    this.mapController.setCamera({
      low: [bound.latLo().degrees(), bound.lngLo().degrees()],
      high: [bound.latHi().degrees(), bound.lngHi().degrees()],
      brand: 'LatLngRect',
    });
  }

  deselectClicked(): void {
    this.clickFeature(undefined);
  }

  fieldIconClicked(e: CorgiEvent<typeof FIELD_ICON_CLICKED>): void {
    const id = e.detail.field.id;
    const selected = this.selected !== undefined ? this.store.get(this.selected) : undefined;
    if (!selected) {
      return;
    }

    const counts = new Map<string, number>();
    for (const feature of this.store.all()) {
      const value = feature.data[id];
      const icon = typeof value === 'string' ? pointIcon(value) : DEFAULT_ICON;
      if (icon !== DEFAULT_ICON) {
        counts.set(icon, (counts.get(icon) ?? 0) + 1);
      }
    }

    const current = selected.data[id];
    const fill =
        selected.kind === 'point' ? pointLook(this.store.schema(), selected.data)?.fill : undefined;
    this.dialog.display(IconPickerDialog({
      color: fill ?? DEFAULT_COLOR,
      current: typeof current === 'string' ? current : undefined,
      onChosen: icon => {
        // An upload can finish after somebody closed the picker and selected something else.
        if (this.selected === selected.id) {
          this.editField(id, icon);
        }
      },
      upload: file => this.uploadIcon(file),
      used: [...counts].sort((a, b) => b[1] - a[1]).map(([icon]) => icon),
    })).catch(() => {});
  }

  folderChanged(e: CorgiEvent<typeof CHANGED>): void {
    const folder = e.detail.value;
    if (folder) {
      this.expand([folder]);
    }
    this.editSelectedData(data => ({...data, folder_id: folder || undefined}));
  }

  deleteClicked(): void {
    const feature = this.selected !== undefined ? this.store.get(this.selected) : undefined;
    if (!feature || this.store.isReadOnly()) {
      return;
    }

    // A folder's descendants come deepest first and then the folder itself, so each folder is
    // empty by the time its delete lands.
    const doomed =
        feature.kind === 'folder' ? [...this.store.descendants(feature.id), feature] : [feature];
    const count = doomed.length - 1;
    const items = `${count} ${count === 1 ? 'item' : 'items'}`;
    const confirmed =
        count > 0
            ? this.dialog.display(ConfirmDeleteDialog({
              message: `This also deletes the ${items} inside it.`,
              title: 'Delete this folder?',
            }))
            : Promise.resolve();
    confirmed.then(() => {
      this.store.apply(doomed.map(f => ({id: f.id, before: snapshot(f), after: undefined})));
      this.select(undefined);
    }, () => {});
  }

  // Moves the sun and moon as the timeline drags, and leaves the weather for timeChosen because
  // each forecast step the drag passes would fetch a new set of tiles.
  timeScrubbed(e: CorgiEvent<typeof DOM_MOUSE>): void {
    const at = (e.actionElement.element() as HTMLInputElement).valueAsNumber;
    this.player.stop();
    this.catalog.previewTime(at);
    this.updateTimeline({at, playing: false});
  }

  timeChosen(e: CorgiEvent<typeof DOM_MOUSE>): void {
    const at = (e.actionElement.element() as HTMLInputElement).valueAsNumber;
    this.setTime(at);
  }

  // The input gives a local time without a zone, which is in the zone the timeline shows.
  timeTyped(e: CorgiEvent<typeof DOM_MOUSE>): void {
    const at =
        fromWallClock(
            (e.actionElement.element() as HTMLInputElement).value, this.state.time.zone);
    if (Number.isNaN(at)) {
      return;
    }
    this.setTime(at);
  }

  timeNowClicked(): void {
    this.player.stop();
    this.setTime(undefined, {now: Date.now(), playing: false});
  }

  timePlayClicked(): void {
    const time = this.state.time;
    if (time.playing) {
      this.player.stop();
      this.updateTimeline({playing: false});
      return;
    }
    const at = time.at ?? Date.now();
    this.updateTimeline({at: at === time.end ? time.start : at, playing: true});
    this.player.start();
  }

  // The layer already moved its pins, so configuring it with them changes nothing.
  onSkyPinsMoved(e: CorgiEvent<typeof SKY_PINS_MOVED>): void {
    const entry = checkExists(findLayerNode(this.state.layers.nodes, SKY_LAYER_ID)) as LayerEntry;
    this.setLayerValues(entry, {...entry.values, ...e.detail});
  }

  onMove(e: CorgiEvent<typeof MAP_MOVED>): void {
    const {center, zoom} = e.detail;
    const picked = this.picked?.camera;
    if (
        picked
            && (picked.lat !== center.latDegrees()
                || picked.lng !== center.lngDegrees()
                || picked.zoom !== zoom)) {
      // Another menu may have replaced the pick menu since, and that one stays.
      if (this.openedMenu === this.picked?.menu) {
        this.closeMenu();
      }
      this.picked = undefined;
    }
    const url = new URL(window.location.href);
    url.searchParams.set('lat', center.latDegrees().toFixed(7));
    url.searchParams.set('lng', center.lngDegrees().toFixed(7));
    url.searchParams.set('zoom', zoom.toFixed(3));
    this.history.silentlyReplaceUrl(url.toString());
    this.updateZone(center);
  }

  // A group's row opens it and a layer's row shows it, since a group has nothing else to click for.
  layerRowClicked(e: CorgiEvent<typeof DOM_MOUSE>): void {
    const node = this.layerNodeOf(e);
    if (node.kind === 'group') {
      this.toggleLayerExpanded(node.id);
    } else {
      this.setLayerEnabled(node.id, !node.enabled);
    }
  }

  layerExpandToggled(e: CorgiEvent<typeof DOM_MOUSE>): void {
    this.toggleLayerExpanded(this.layerNodeOf(e).id);
  }

  layerVisibilityToggled(e: CorgiEvent<typeof DOM_MOUSE>): void {
    const node = this.layerNodeOf(e);
    this.setLayerEnabled(node.id, !node.enabled);
  }

  layerControlChanged(e: CorgiEvent<typeof DOM_MOUSE>): void {
    const entry = this.layerNodeOf(e);
    if (entry.kind !== 'layer') {
      throw new Error(`Group ${entry.id} has no controls`);
    }

    const key = checkExists(e.actionElement.data('key')).string();
    const control = checkExists(entry.controls.find(c => c.key === key));
    const input = e.actionElement.element() as HTMLInputElement;
    let value;
    if (control.kind === 'checkbox') {
      value = checkExists(e.actionElement.data('on')).string() === 'true';
    } else if (control.kind === 'date') {
      value = input.value;
    } else if (control.kind === 'percent') {
      value = input.valueAsNumber;
      if (!(value >= 0 && value <= 100)) {
        return;
      }
    } else if (control.kind === 'select') {
      value = (e.actionElement.element() as HTMLSelectElement).value;
    } else {
      throw checkExhaustive(control);
    }

    this.setLayerValues(entry, {...entry.values, [key]: value});
    // The Sentinel layers credit the years they search
    this.mapController.copyrightsChanged();
  }

  menubarMenuClicked(e: CorgiEvent<typeof DOM_MOUSE>): void {
    this.menubarMenu(
        checkExists(e.actionElement.data('menu')).string(), e.actionElement.element());
  }

  menubarMenuEntered(e: CorgiEvent<typeof DOM_POINTER>): void {
    const trigger = e.actionElement.element();
    // Only a menubar menu has a trigger, and a context or pick menu stays put.
    if (
        !this.openedMenu?.isOpen
            || this.menuTrigger === undefined
            || this.menuTrigger === trigger) {
      return;
    }

    this.menubarMenu(checkExists(e.actionElement.data('menu')).string(), trigger);
  }

  toolClicked(e: CorgiEvent<typeof ACTION>): void {
    this.setTool(checkExists(e.actionElement.data('tool')).string() as Tool);
  }

  loginClicked(): void {
    this.requireLogin().catch(e => {
      console.error(e);
    });
  }

  private menubarMenu(menu: string, trigger: Element): void {
    if (!this.claimMenu(trigger)) {
      return;
    }

    if (menu === 'file') {
      const listing: Future<ListCollectionsResponse> =
          requestData('lat.trails.DataService/ListCollections', {});
      listing.then(response => {
        const items =
            fileMenu(response, this.state.collection !== undefined, this.store.isReadOnly(), {
              newCollection: () => this.history.goTo('/'),
              openCollection: collection => this.openCollection(collection),
              openSettings: () => this.openSettings(),
              importFiles: () => this.pickImportFiles(),
              exportFiles: () => this.exportFiles(),
            });
        this.openMenu(items, trigger);
      });
    } else if (menu === 'edit') {
      this.openMenu(editMenu(this.store), trigger);
    } else if (menu === 'view') {
      this.openMenu(
          viewMenu(
              getUnitSystem(),
              system => this.setUnitSystem(system),
              this.zoneChoice,
              zone => this.chooseZone(zone)),
          trigger);
    } else if (menu === 'help') {
      this.openMenu(HELP_MENU, trigger);
    } else if (menu === 'user') {
      this.openMenu(
          userMenu(() => {
            this.openAccount();
          }),
          trigger);
    }
  }

  // Leaves a drawing tool along with edit rights, or else it would draw what the store drops.
  private resetStore(features: EditableFeature[], readOnly: boolean, schema?: SavedSchema): void {
    this.store.reset(features, readOnly, schema);
    const tool = this.state.tool;
    if (readOnly && tool !== 'pointer' && tool !== 'measure' && tool !== 'terrain') {
      this.setTool('pointer');
    }
  }

  // Deactivates before activating, because the drawing layers share the index's routing switch.
  private setTool(tool: Tool): void {
    this.stopJoining();
    this.lineLayer.setActive(false);
    this.polygonLayer.setActive(false);
    this.measureLayer.setActive(false);
    this.terrainLayer.setActive(false);
    this.pointLayer.setActive(tool === 'point');
    this.markerLayer.setActive(tool === 'marker');
    this.featureLayer.setInteractive(tool === 'pointer');
    this.featureLayer.setPointsDraggable(tool === 'point');
    this.catalog.setInteractive(tool === 'pointer');
    // Refreshes the credits, because the drawing layers' only count while they are active
    this.setMapLayers(this.state.layers.nodes);
    if (tool === 'line') {
      this.lineLayer.setActive(true);
    } else if (tool === 'polygon') {
      this.polygonLayer.setActive(true);
    } else if (tool === 'measure') {
      this.measureLayer.setActive(true);
    } else if (tool === 'terrain') {
      this.terrainLayer.setActive(true);
    }
    this.updateState({
      ...this.state,
      tool,
    });
  }

  // Skipped while typing, so that the browser's own undo still works in text fields.
  private keyPressed(e: KeyboardEvent): void {
    // A map layer already handled it, like Delete removing a vertex from the line being edited
    if (e.defaultPrevented) {
      return;
    }

    const target = e.target;
    if (
        target instanceof HTMLInputElement
            || target instanceof HTMLTextAreaElement
            || target instanceof HTMLSelectElement
            || (target instanceof HTMLElement && target.isContentEditable)) {
      return;
    }

    // Menus close on keyup, so one still open at keydown is what Escape is for.
    if (e.key === 'Escape' && this.state.tool === 'pointer' && !this.openedMenu?.isOpen) {
      if (this.state.joining !== undefined) {
        this.stopJoining();
      } else {
        this.clickFeature(undefined);
      }
      e.preventDefault();
      return;
    }

    if ((e.key === 'Delete' || e.key === 'Backspace') && this.state.tool === 'pointer') {
      if (this.selected !== undefined) {
        this.deleteClicked();
        e.preventDefault();
      }
      return;
    }

    if (!(e.ctrlKey || e.metaKey) || e.altKey) {
      return;
    }

    const key = e.key.toLowerCase();
    if (key === 'z' && !e.shiftKey) {
      this.store.undo();
    } else if (key === 'y' || (key === 'z' && e.shiftKey)) {
      this.store.redo();
    } else {
      return;
    }
    e.preventDefault();
  }

  // Without elevations, because the profile samples the DEM along a line that has none, and
  // elevations read at the vertices alone would draw each segment as a straight slope.
  private addLine(latLngE7: Int32Array, data: FeatureData): void {
    const line: EditableLine = {
      kind: 'line',
      id: crypto.randomUUID(),
      version: 0n,
      data,
      latLngE7,
      elevationCentimeters: undefined,
      timeSeconds: undefined,
    };
    this.addFeature(line);
  }

  // Returns the field that points take their icon from, if it's a field of type icon. Only then
  // can a photo become a point's icon.
  private getImageIconField(): string|undefined {
    const schema = this.store.schema();
    const channel = channelOf(schema, 'point', 'icon');
    return channel.kind === 'field' && getField(schema, 'point', channel.key)?.type === 'icon'
        ? channel.key
        : undefined;
  }

  private uploadIcon(file: File): Promise<string> {
    return this.currentCollection()
        .then(collection => uploadImage(collection, file))
        .then(({id}) => getImageIcon(id));
  }

  // Starts a feature somebody made in the selected folder, or else beside the selected feature,
  // with its fields' defaults, and selects it.
  private addFeature(feature: EditableFeature): void {
    const parent = this.getFolderAt(this.selected);
    if (parent !== undefined) {
      feature.data.folder_id = parent;
    }
    feature.data = withDefaults(this.store.schema(), feature);
    this.store.apply([{id: feature.id, before: undefined, after: feature}]);
    this.reveal(feature.id);
  }

  // Resolves undefined when the tiles could not be fetched.
  private sampleVertices(latLngE7: Int32Array): Promise<Int32Array|undefined> {
    const degrees = Float64Array.from(latLngE7, e7 => e7 / 1e7);
    return this.elevations.sample(degrees, profileSamples(degrees).zoom)
        .then(meters => Int32Array.from(meters, m => Math.round(m * 100)), () => undefined);
  }

  private measureChanged(): void {
    this.measureProfiler.changed(
        this.state.tool === 'terrain' ? this.terrainLayer.routeStatus() : undefined);
  }

  // The line, measure, and terrain tools share the panel and its state, and only one is active at a
  // time.
  private measuringLayer(): DrawingLayer {
    const tool = this.state.tool;
    return tool === 'line' ? this.lineLayer
        : tool === 'terrain' ? this.terrainLayer
        : this.measureLayer;
  }

  private drawingLayer(): DrawingLayer|undefined {
    const tool = this.state.tool;
    return tool === 'polygon' ? this.polygonLayer
        : tool === 'line' || tool === 'measure' || tool === 'terrain' ? this.measuringLayer()
        : undefined;
  }

  // Compares before updating, because every pointer move changes the layer without moving a vertex.
  private drawnAreaChanged(): void {
    const drawing = this.polygonLayer.placedPoints();
    const ring = drawing.length > 0 ? toDegrees(drawing) : undefined;
    const shown = this.state.drawnArea;
    if (
        ring?.length === shown?.length
            && (!ring || !shown || ring.every((v, i) => v === shown[i]))) {
      return;
    }
    this.updateState({...this.state, drawnArea: ring});
  }

  // Ignores what isn't a positive number, like an emptied field.
  private setGrade(e: CorgiEvent<typeof DOM_MOUSE>, key: keyof GradeLimitState): void {
    const percent = Number((e.actionElement.element() as HTMLInputElement).value);
    if (!(percent > 0) || percent === this.state.grades[key]) {
      return;
    }

    this.updateState({...this.state, grades: {...this.state.grades, [key]: percent}});
    this.terrainLayer.reroute();
  }

  // A folder itself, or else the folder a feature is in
  private getFolderAt(id: string|undefined): string|undefined {
    const feature = id !== undefined ? this.store.get(id) : undefined;
    return feature?.kind === 'folder' ? feature.id : feature && this.store.parentOf(feature);
  }

  private setHovered(id: string|undefined): void {
    if (id === this.state.features.hovered) {
      return;
    }

    this.featureLayer.setHovered(id);
    this.updateState({
      ...this.state,
      features: {
        ...this.state.features,
        hovered: id,
        hoveredAncestors: new Set(this.ancestorsOf(id)),
      },
    });
  }

  private ancestorsOf(id: string|undefined): string[] {
    const ancestors = [];
    let at = id !== undefined ? this.store.get(id) : undefined;
    while (at) {
      const parent = this.store.parentOf(at);
      if (parent === undefined) {
        break;
      }
      ancestors.push(parent);
      at = this.store.get(parent);
    }
    return ancestors;
  }

  // Closes whatever map layer object is open, because the dialog shows one thing at a time. The
  // profile marker goes too, because its chart leaves without a pointerleave, and so does Join
  // with, because it joins onto the line that was selected.
  private select(id: string|undefined): void {
    this.stopJoining();
    this.selected = id;
    this.featureLayer.setSelected(id);
    this.featureLayer.setMarker(undefined);
    this.inspected?.unmark();
    this.inspected = undefined;
    if (this.state.inspected) {
      this.updateState({...this.state, inspected: undefined});
    }
    this.refreshFeatureList();
  }

  // An inspected object's profile belongs to openObject, and a marker stroke has none because it
  // marks up the map rather than following the ground.
  private lineProfileFor(live: EditableFeature|undefined): LineProfileState|undefined {
    if (isRouteLine(live)) {
      return this.lineProfiler.forLine(live);
    } else if (this.state.inspected?.kind === 'line') {
      return this.state.lineProfile;
    }
    this.lineProfiler.forget();
    return undefined;
  }

  searchChanged(e: CorgiEvent<typeof CHANGED>): void {
    this.updateState({...this.state, search: {...this.state.search, query: e.detail.value}});
    this.searchQuery.trigger();
  }

  searchFocused(): void {
    this.searchQuery.trigger();
  }

  searchBlurred(): void {
    this.searchQuery.close();
  }

  searchKeyPressed(e: CorgiEvent<typeof PRESSED>): void {
    const {results, highlighted} = this.state.search;
    if (!results) {
      return;
    }

    if (e.detail.key === 'Escape') {
      this.searchBlurred();
    } else if (e.detail.key === 'ArrowDown' || e.detail.key === 'ArrowUp') {
      const step = e.detail.key === 'ArrowDown' ? 1 : -1;
      const next = Math.max(0, Math.min(results.length - 1, highlighted + step));
      this.updateState({...this.state, search: {...this.state.search, highlighted: next}});
    }
  }

  searchSubmitted(): void {
    const {results, highlighted} = this.state.search;
    const hit = results?.[highlighted];
    if (hit) {
      this.pickSearchHit(hit);
    }
  }

  searchResultPicked(e: CorgiEvent<typeof DOM_POINTER>): void {
    const index = Number(checkExists(e.actionElement.data('index')).string());
    const hit = this.state.search.results?.[index];
    if (hit) {
      this.pickSearchHit(hit);
    }
  }

  private pickSearchHit(hit: SearchHit): void {
    this.searchQuery.close();
    this.fitCamera([...hit.low, ...hit.high]);
  }

  // Merged per field, so that dragging across the color picker is one undo
  private editField(id: string, value: boolean|number|string|undefined): void {
    this.editSelectedData(data => ({...data, [id]: value}), `field:${id}`);
  }

  private editSelectedData(update: (data: FeatureData) => FeatureData, mergeKey?: string): void {
    const live = this.selected !== undefined ? this.store.get(this.selected) : undefined;
    if (!live) {
      return;
    }

    const before = snapshot(live);
    const after = {...snapshot(live), data: stripUndefined(update({...live.data}))};
    // Merged per feature, so that typing in one name and then another stay two undos
    this.store.apply(
        [{id: live.id, before, after}],
        mergeKey !== undefined ? `${mergeKey}:${live.id}` : undefined);
  }

  // The folder a drop onto this row lands in. A row that is not a folder passes it to the folder
  // it is in, so dropping onto a sibling moves nothing.
  private dropFolderFor(e: CorgiEvent<typeof DOM_MOUSE>): string|undefined {
    return this.getFolderAt(checkExists(e.actionElement.data('id')).string());
  }

  // Whether the dragged feature may go into folder. A folder cannot move into itself or anything
  // under it.
  private canDropInto(folder: string|undefined): boolean {
    const dragged = this.draggedFeature;
    if (dragged === undefined) {
      return false;
    }
    return folder === undefined
        || (folder !== dragged && !this.ancestorsOf(folder).includes(dragged));
  }

  // Marks folder as the target when the drop is allowed. The browser only drops where dragover
  // called preventDefault.
  private dragOver(e: CorgiEvent<typeof DOM_MOUSE>, folder: string|undefined): void {
    if (isFileDrag(e)) {
      this.filesDraggedOver(e);
      return;
    }
    if (!this.canDropInto(folder)) {
      this.setDropTarget(undefined);
      return;
    }

    const drag = e.detail as DragEvent;
    drag.preventDefault();
    if (drag.dataTransfer) {
      drag.dataTransfer.dropEffect = 'move';
    }
    this.setDropTarget({folder});
  }

  private drop(e: CorgiEvent<typeof DOM_MOUSE>, folder: string|undefined): void {
    if (isFileDrag(e)) {
      this.filesDropped(e);
      return;
    }
    const dragged = this.draggedFeature;
    const live = dragged !== undefined ? this.store.get(dragged) : undefined;
    if (!live || !this.canDropInto(folder)) {
      return;
    }

    e.detail.preventDefault();
    this.draggedFeature = undefined;
    this.setDropTarget(undefined);
    if (folder !== undefined) {
      this.expand([folder]);
    }
    if (this.store.parentOf(live) === folder) {
      return;
    }

    const before = snapshot(live);
    const after = {...snapshot(live), data: stripUndefined({...live.data, folder_id: folder})};
    this.store.apply([{id: live.id, before, after}]);
  }

  private setFileDragged(fileDragged: boolean): void {
    if (this.state.fileDragged !== fileDragged) {
      this.updateState({...this.state, fileDragged});
    }
  }

  private setDropTarget(target: {folder: string|undefined}|undefined): void {
    const was = this.state.features.dropTarget;
    if (target?.folder !== was?.folder || !target !== !was) {
      this.updateState({...this.state, features: {...this.state.features, dropTarget: target}});
    }
  }

  // Follows the pointer with the length drawn so far, which includes the segment to it. A polygon's
  // includes the way back to its first vertex, so it reads as the perimeter.
  private drawnLengthChanged(): void {
    const layer = this.drawingLayer();
    const pointer = this.pointer;
    const drawnLength =
        layer?.followsPointer() && pointer
            ? {x: pointer[0], y: pointer[1], meters: pathLengthMeters(toDegrees(layer.points()))}
            : undefined;
    const shown = this.state.drawnLength;
    if (
        drawnLength?.x === shown?.x
            && drawnLength?.y === shown?.y
            && drawnLength?.meters === shown?.meters) {
      return;
    }
    this.updateState({...this.state, drawnLength});
  }

  private stopJoining(): void {
    if (this.state.joining !== undefined) {
      this.updateState({...this.state, joining: undefined});
    }
  }

  // Splits at a vertex, adding one first when the spot is partway along a segment.
  private splitLineAt(id: string, spot: LineSpot): void {
    const live = this.store.get(id);
    if (live?.kind !== 'line') {
      return;
    }

    const line =
        spot.kind === 'segment' ? insertVertex(live, spot.index, spot.latE7, spot.lngE7) : live;
    const halves =
        splitLine(
            line, spot.index, crypto.randomUUID(), titleField(this.store.schema(), line));
    if (!halves) {
      return;
    }

    const [first, second] = halves;
    const changes = [
      {id: live.id, before: snapshot(live), after: first},
      {id: second.id, before: undefined, after: second},
    ];
    if (spot.kind === 'segment') {
      this.applyPlacingVertices(
          changes, [{id: live.id, index: spot.index}, {id: second.id, index: 0}]);
    } else {
      this.store.apply(changes);
    }
  }

  // Applies an edit that moved or added vertices, then gives each one on a line with elevations
  // of its own the DEM's elevation as part of the same edit. Every other line samples the DEM for
  // its profile anyway.
  //
  // An edit landing before the DEM answers keeps the patch from merging, so the patch becomes an
  // undo of its own. The DEM usually answers well before anyone can edit again.
  private applyPlacingVertices(
      changes: Change[], placed: Array<{id: string; index: number}>): void {
    const mergeKey = `placed:${crypto.randomUUID()}`;
    this.store.apply(changes, mergeKey);
    const targets = placed.flatMap(({id, index}) => {
      const line = this.store.get(id);
      return line?.kind === 'line' && line.elevationCentimeters
          ? [{id, index, latLngE7: line.latLngE7}]
          : [];
    });
    if (targets.length === 0) {
      return;
    }

    const latLngE7 = Int32Array.from(
        targets.flatMap(({index, latLngE7}) => [latLngE7[2 * index], latLngE7[2 * index + 1]]));
    this.sampleVertices(latLngE7).then(centimeters => {
      if (!centimeters) {
        return;
      }

      const patches = targets.flatMap(({id, index, latLngE7}, i) => {
        const live = this.store.get(id);
        // Geometry edits replace the array rather than writing through it, so the same array means
        // the line has kept its shape since.
        if (live?.kind !== 'line' || live.latLngE7 !== latLngE7 || !live.elevationCentimeters) {
          return [];
        }

        const elevationCentimeters = live.elevationCentimeters.slice();
        elevationCentimeters[index] = centimeters[i];
        return [{id, before: snapshot(live), after: {...snapshot(live), elevationCentimeters}}];
      });
      this.store.apply(patches, mergeKey);
    });
  }

  private editShape(id: string, edit: (shape: EditableShape) => EditableShape|undefined): void {
    const live = this.store.get(id);
    const after = isShape(live) ? edit(live) : undefined;
    if (live && after) {
      this.store.apply([{id, before: snapshot(live), after}]);
    }
  }

  // Selects the copy, because it lands exactly on the original and would otherwise look like
  // nothing happened.
  private duplicateFeature(id: string): void {
    const live = this.store.get(id);
    if (!live) {
      return;
    }

    const copy = {...snapshot(live), id: crypto.randomUUID(), version: 0n};
    const field = titleField(this.store.schema(), copy);
    const title = field !== undefined ? copy.data[field] : undefined;
    if (field !== undefined && typeof title === 'string' && title) {
      copy.data[field] = `${title} copy`;
    }
    this.addFeature(copy);
  }

  // Hands the list copies, because the store edits features in place and corgi skips rendering a
  // component whose props are the same objects as last time.
  private refreshFeatureList(): void {
    const live = this.selected !== undefined ? this.store.get(this.selected) : undefined;
    const selected = live && snapshot(live);
    this.updateState({
      ...this.state,
      features: {
        ...this.state.features,
        all: [...this.store.all()].map(snapshot),
        schema: this.store.schema(),
        selected,
        selectedDescendants:
            selected?.kind === 'folder' ? this.store.descendants(selected.id).length : 0,
        hoveredAncestors: new Set(this.ancestorsOf(this.state.features.hovered)),
      },
      lineProfile: this.lineProfileFor(live),
    });
  }

  // Shows the name from the listing while the rest loads.
  private openCollection(collection: Collection): void {
    if (collection.id === this.routedCollection) {
      return;
    }

    // The route's render reads the listing's bound for its camera, so the state has to land first.
    this.updateState({...this.state, collection}).then(() => {
      this.history.goTo(`/collection/${collection.id}`);
    });
  }

  // One undo for every schema edit made while the dialog is open
  private openSettings(): void {
    const collection = this.state.collection;
    if (!collection) {
      return;
    }

    let current = collection;
    const mergeKey = `settings:${crypto.randomUUID()}`;
    this.dialog.display(CollectionSettingsDialog({
      collection,
      editable: !this.store.isReadOnly(),
      schema: this.store.schema(),
      onDelete: () => this.deleteCollection(current),
      onSchemaChanged: schema => {
        this.store.setSchema(schema, mergeKey);
      },
      onUpdated: updated => {
        current = updated;
        if (this.state.collection?.id === updated.id) {
          this.updateState({...this.state, collection: updated});
        }
      },
      uploadIcon: file => this.uploadIcon(file),
    })).catch(() => {});
  }

  private deleteCollection(collection: Collection): Promise<boolean> {
    const count = [...this.store.all()].length;
    const message =
        count > 0
            ? `This deletes the ${count} ${count === 1 ? 'item' : 'items'} in it for everyone `
                + 'it is shared with, and cannot be undone.'
            : 'This cannot be undone.';
    return this.dialog.display(ConfirmDeleteDialog({message, title: `Delete ${collection.name}?`}))
        .then(() => {
          const deleted: Future<DeleteCollectionResponse> =
              requestData('lat.trails.DataService/DeleteCollection', {id: collection.id});
          return deleted;
        }, () => undefined)
        .then(response => {
          // Replaced rather than pushed so that back doesn't land on the deleted collection.
          if (response && this.state.collection?.id === collection.id) {
            this.history.replaceTo('/');
          }
          if (response) {
            this.toast.show(`Deleted ${collection.name}`);
          }
          return !!response;
        })
        .catch(e => {
          console.error(e);
          this.toast.show(`Deleting ${collection.name} failed`);
          return false;
        });
  }

  // Both dialogs reject when dismissed, so only confirming both reaches DeleteAccount.
  private openAccount(): void {
    const account: Future<GetAccountResponse> =
        requestData('lat.trails.DataService/GetAccount', {});
    Promise.all([this.state.self, account])
        .then(([self, response]) =>
            this.dialog.display(AccountDialog({account: response, user: checkExists(self.user)}))
                .then(() => this.dialog.display(ConfirmDeleteAccountDialog({account: response})))
                .then(() => this.deleteAccount(), () => {}))
        .catch(e => {
          console.error(e);
        });
  }

  private deleteAccount(): Promise<void> {
    const deleted: Future<DeleteAccountResponse> =
        requestData('lat.trails.DataService/DeleteAccount', {});
    return deleted.then(
        () => {
          // The login cookie still names the deleted user until /logout clears it.
          window.location.href = '/logout';
        },
        e => {
          console.error(e);
          this.dialog.display(DeleteAccountFailedDialog({})).catch(() => {});
        });
  }

  private loadCollection(id: string): void {
    this.collectionGeneration += 1;
    const generation = this.collectionGeneration;
    const hidden = this.state.features.hidden;
    const layerEdits = this.layerEdits;
    this.updateState({...this.state, collectionStatus: 'loading'});
    const loading: Future<GetCollectionResponse> =
        requestData('lat.trails.DataService/GetCollection', {id});
    loading
        .then(response => {
          if (generation !== this.collectionGeneration) {
            return;
          }
          this.updateState({
            ...this.state,
            collection: response.collection,
            collectionStatus: 'loaded',
          });
          // A toggle made since the request, like on the rows the server rendered, is newer than
          // what the response read.
          if (this.state.features.hidden === hidden) {
            this.setHidden(response.hiddenFeatureIds);
          }
          if (this.layerEdits === layerEdits) {
            this.setLayerSettings(
                response.layers ? JSON.parse(response.layers) as LayerSettings : {});
          }
          const features = featuresFromProto(response);
          const schema = parseCollectionSchema(response.schema);
          this.expand(topLevelFolderIds(features, schema, this.state.features.hidden));
          this.resetStore(
              features, !canEdit(response.collection), {schema, version: response.schemaVersion});
        })
        .catch(e => {
          console.error(e);
          if (generation === this.collectionGeneration) {
            this.updateState({...this.state, collectionStatus: 'failed'});
          }
        });
  }

  private pickImportFiles(): void {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.geojson,.gpx,.json,application/geo+json,application/gpx+xml,image/*';
    input.multiple = true;
    input.addEventListener('change', () => {
      this.importFiles(Array.from(input.files ?? []));
    });
    input.click();
  }

  // Applies everything as one edit, so that a single undo takes back everything it added. Photos
  // upload UPLOADS_IN_FLIGHT at a time so that one person's drop leaves the API server's other
  // encodes to everybody else.
  private importFiles(files: File[], dropped?: S2LatLng): void {
    const generation = this.collectionGeneration;
    const stale = () => generation !== this.collectionGeneration;
    const results: Array<{changes: Change[]; failed?: string}> = [];
    let imported = 0;
    const describe = (verb: string, count: number) =>
        files.length === 1
            ? `${verb} ${files[0].name}`
            : `${verb} ${count} of ${files.length} files`;
    const progress = this.toast.start(describe('Importing', 0), /* fraction= */ 0);
    const importAll = (queue: File[], tracks: EditableLine[]) => {
      const importNext = async (): Promise<void> => {
        for (let file = queue.shift(); file && !stale(); file = queue.shift()) {
          const result = await this.importOne(file, dropped, tracks);
          results.push(result);
          if (!result.failed) {
            imported += 1;
          }
          progress.update(describe('Imported', imported), results.length / files.length);
        }
      };
      return Promise.all(Array.from({length: UPLOADS_IN_FLIGHT}, importNext));
    };
    // Tracks import first so that photos dropped alongside them can be placed along them.
    const isImage = (file: File) => file.type.startsWith('image/');
    importAll(files.filter(f => !isImage(f)), []).then(() => {
      const tracks = [
        ...this.store.lines(),
        ...results.flatMap(r => r.changes.flatMap(c => c.after?.kind === 'line' ? [c.after] : [])),
      ];
      return importAll(files.filter(isImage), tracks);
    }).then(() => {
      // Another collection was opened meanwhile, and these changes belong to the one before.
      if (stale()) {
        progress.dismiss();
        return;
      }
      this.store.apply(results.flatMap(r => r.changes));
      const failed = results.flatMap(r => r.failed ? [r.failed] : []);
      if (imported === 0) {
        progress.dismiss();
      } else if (failed.length > 0) {
        progress.finish(describe('Imported', imported));
      } else {
        progress.finish(
            files.length === 1 ? describe('Imported', 1) : `Imported ${imported} files`);
      }
      if (failed.length > 0) {
        this.dialog.display(ImportFailedDialog({files: failed})).catch(() => {});
      }
    });
  }

  private exportFiles(): void {
    const schema = this.store.schema();
    this.dialog.display(ExportDialog({
      features: [...this.store.all()],
      schema,
      onExport: (format, features) => {
        if (format === 'gpx') {
          this.download(toGpx(features, schema), 'gpx', 'application/gpx+xml');
        } else {
          this.download(toGeoJson(features, schema), 'geojson', 'application/geo+json');
        }
      },
    })).catch(() => {});
  }

  private download(text: string, extension: string, type: string): void {
    const url = URL.createObjectURL(new Blob([text], {type}));
    const link = document.createElement('a');
    link.href = url;
    link.download = `${this.state.collection?.name || 'trails'}.${extension}`;
    link.click();
    URL.revokeObjectURL(url);
  }

  // Reports the file's name when it yielded nothing, so that importing several files names every
  // one that failed in a single dialog.
  private importOne(file: File, dropped: S2LatLng|undefined, tracks: EditableLine[]):
      Promise<{changes: Change[]; failed?: string}> {
    const changes =
        file.type.startsWith('image/')
            ? this.importImage(file, dropped, tracks)
            : this.importFeatures(file);
    return changes.then(
        changes => changes.length > 0 ? {changes} : {changes, failed: file.name},
        e => {
          console.error(e);
          return {changes: [], failed: file.name};
        });
  }

  // Wraps the file's features in a folder named after it, so that importing a pile of files does
  // not bury the collection's root.
  private importFeatures(file: File): Promise<Change[]> {
    return file.text().then(text => {
      const features = parseImport(text);
      // A GPX carrying only routes parses and still leaves nothing to draw, which looks the same
      // to somebody who picked a file and watched the map not change.
      if (features.length === 0) {
        return [];
      }

      const folder: EditableFeature = {
        kind: 'folder',
        id: crypto.randomUUID(),
        version: 0n,
        data: {name: file.name.replace(/\.[^.]*$/, '')},
      };
      const schema = this.store.schema();
      for (const feature of features) {
        feature.data.folder_id ??= folder.id;
        adoptImported(schema, feature);
        feature.data = withDefaults(schema, feature);
      }
      return [folder, ...features].map(f => ({id: f.id, before: undefined, after: f}));
    });
  }

  // Makes a point that shows the photo where it was geotagged, or else where a track was when the
  // photo was taken, or else where it was dropped on the map, or else in the middle of the map.
  private importImage(file: File, dropped: S2LatLng|undefined, tracks: EditableLine[]):
      Promise<Change[]> {
    const field = this.getImageIconField();
    if (field === undefined) {
      return Promise.reject(new Error('Points have no icon field to show a photo in'));
    }
    return this.currentCollection()
        .then(collection => uploadImage(collection, file))
        .then(({capturedEpochSeconds, id, location}) => {
          const tracked =
              location === undefined && capturedEpochSeconds !== undefined
                  ? getPositionAt(tracks, Number(capturedEpochSeconds))
                  : undefined;
          const at =
              location
                  ? S2LatLng.fromDegrees(location.latDegrees, location.lngDegrees)
                  : dropped ?? this.mapController.camera.center;
          const point: EditableFeature = {
            kind: 'point',
            id: crypto.randomUUID(),
            version: 0n,
            data: {[field]: getImageIcon(id)},
            latE7: tracked?.latE7 ?? Math.round(at.latDegrees() * 1e7),
            lngE7: tracked?.lngE7 ?? Math.round(at.lngDegrees() * 1e7),
            elevationCentimeters: tracked?.elevationCentimeters,
          };
          point.data = withDefaults(this.store.schema(), point);
          return [{id: point.id, before: undefined, after: point}];
        });
  }

  private save(entries: SaveEntry[]): Promise<bigint> {
    return this.currentCollection()
        .then(collectionId => {
          const saved: Future<SaveResponse> =
              requestData('lat.trails.DataService/Save', {
                collectionId,
                writes:
                    entries.map(e =>
                        e.kind === 'schema'
                            ? schemaToWrite(e.schema)
                            : toWrite(e.op, e.feature)),
              });
          return saved;
        })
        .then(response => response.version);
  }

  // Dismissing the dialog arms it again, so a later failure is not silent.
  private warnUnsaved(): void {
    if (this.warnedUnsaved) {
      return;
    }

    this.warnedUnsaved = true;
    const dismissed = () => {
      this.warnedUnsaved = false;
    };
    this.dialog.display(SaveFailedDialog({})).then(dismissed, dismissed);
  }

  // The collection in the URL, even while it loads, or else a new one.
  private currentCollection(): Promise<string> {
    if (this.routedCollection !== undefined) {
      return Promise.resolve(this.routedCollection);
    }

    this.creatingCollection ??= this.requireLogin('save')
        .then(() => {
          const created: Future<CreateCollectionResponse> =
              requestData(
                  'lat.trails.DataService/CreateCollection', {
                    name: newCollectionName(),
                    schema: JSON.stringify(this.store.schema()),
                  });
          return created;
        })
        .then(response => {
          const collection = checkExists(response.collection);
          this.updateState({
            ...this.state,
            collection,
          });
          this.routedCollection = collection.id;
          this.history.replaceTo(`/collection/${collection.id}`);
          this.saveViewState();
          this.toast.show('Created a new collection');
          return collection.id;
        })
        .finally(() => {
          this.creatingCollection = undefined;
        });
    return this.creatingCollection;
  }

  private setHidden(ids: Iterable<string>): void {
    const hidden = new Set(ids);
    this.featureLayer.setHidden(hidden);
    this.updateState({...this.state, features: {...this.state.features, hidden}});
  }

  private expand(ids: Iterable<string>): void {
    const expanded = new Set([...this.state.features.expanded, ...ids]);
    this.updateState({...this.state, features: {...this.state.features, expanded}});
  }

  // Viewers get no view state, so they start from the owner's every time they open the collection.
  private saveViewState(): void {
    const collection = this.state.collection;
    // File > Open puts the listing's collection in state before its hidden ids load.
    if (!canEdit(collection) || this.state.collectionStatus !== 'loaded') {
      return;
    }

    const request = {
      collectionId: collection.id,
      hiddenFeatureIds: [...this.state.features.hidden],
      layers: JSON.stringify(diffLayerSettings(this.state.layers.nodes, this.layerDefaults)),
    };
    this.viewStateWrites = this.viewStateWrites
        .then(() => {
          const saved: Future<SetViewStateResponse> =
              requestData('lat.trails.DataService/SetViewState', request);
          return saved;
        })
        .then(() => {}, e => {
          console.error(e);
        });
  }

  private requireLogin(reason?: LoginReason): Promise<void> {
    // Somebody already signed in whose GetCurrentUser has not landed yet gets a popup if we go by
    // finished, so wait the fetch out instead.
    return this.state.self.then(response => {
      if (response.user) {
        return;
      }

      // Callers wait on this, so it has to settle even when the user fetch fails, or else the
      // write queue behind it never runs again.
      this.login ??= this.loginPopup.open(reason)
          .then(response => {
            this.signedIn(response);
            if (!response.user) {
              throw new Error('Nobody signed in');
            }
          })
          .finally(() => {
            this.login = undefined;
          });
      return this.login;
    });
  }

  private signedIn(response: GetCurrentUserResponse): void {
    this.updateState({...this.state, self: resolvedFuture(response)});
    if (!response.user) {
      return;
    }

    // Now that the user is logged in, they may have edit rights to the open collection. So reload
    // if they're currently in a read-only mode.
    const open = this.routedCollection;
    if (open !== undefined && this.store.isReadOnly()) {
      this.loadCollection(open);
    } else {
      this.saves.flush();
    }
  }

  // Closes whatever menu is open and makes trigger's the one to open. Answers false when trigger's
  // own menu was the open one, so that clicking it again only closes it.
  private claimMenu(trigger: Element): boolean {
    const toggled = this.openedMenu?.isOpen && this.menuTrigger === trigger;
    this.closeMenu();
    this.menuTrigger = toggled ? undefined : trigger;
    return !toggled;
  }

  private closeMenu(): void {
    this.openedMenu?.close();
    this.openedMenu = undefined;
    this.menuTrigger = undefined;
  }

  // Opens a menu at px, which is relative to the map's canvas.
  private openContextMenu(
      items: MenuEntries, px: Vec2, classes: MenuClassNames = MENU_CLASSES): MenuHandle {
    this.closeMenu();
    const canvas = checkExists(this.root.querySelector('canvas')).getBoundingClientRect();
    const root = this.root.getBoundingClientRect();
    const menu =
        this.menu.open(
            items,
            {x: canvas.left - root.left + px[0], y: canvas.top - root.top + px[1]},
            this.root,
            {anchor: 'top_left', classes});
    this.openedMenu = menu;
    return menu;
  }

  // Skips a trigger that lost its claim or whose menu an earlier load already opened.
  private openMenu(items: MenuEntries, trigger: Element): void {
    if (this.menuTrigger !== trigger || this.openedMenu?.isOpen) {
      return;
    }

    const bound = trigger.getBoundingClientRect();
    this.openedMenu =
        this.menu.open(
            items,
            {x: bound.left, y: bound.bottom},
            this.root,
            {anchor: 'top_left', classes: MENU_CLASSES, trigger});
  }

  // The formatters read the unit system from a global rather than from props, so a rerender with
  // caching on would skip every element whose props are unchanged.
  private setUnitSystem(system: UnitSystem): void {
    setUnitSystem(system);
    const nodes =
        updateLayerNode(
            this.state.layers.nodes,
            'topography/contours',
            node => ({...node as LayerEntry, layer: this.catalog.contours[system]}));
    corgi.vdomCaching.disable();
    this.updateState({...this.state, layers: {...this.state.layers, nodes}}).then(() => {
      corgi.vdomCaching.enable();
    });
    this.setMapLayers(nodes);
  }

  private chooseZone(choice: ZoneChoice): void {
    this.zoneChoice = choice;
    writeZoneChoice(choice);
    this.updateZone(this.mapController.camera.center);
  }

  private updateZone(center: S2LatLng): void {
    this.zoneGeneration += 1;
    if (this.zoneChoice === 'device') {
      this.showZone(undefined);
      return;
    }
    const generation = this.zoneGeneration;
    // Rounded to about a kilometer so that the cache answers a map that barely moved
    const loading: Future<GetTimeZoneResponse> =
        requestData('lat.trails.DataService/GetTimeZone', {
          latDegrees: Math.round(center.latDegrees() * 100) / 100,
          lngDegrees: Math.round(center.lngDegrees() * 100) / 100,
        });
    loading.then(response => {
      if (generation !== this.zoneGeneration) {
        return;
      }
      this.showZone(isKnownZone(response.zone) ? response.zone : undefined);
    }, e => {
      console.error(e);
    });
  }

  private showZone(zone: string|undefined): void {
    if (zone === this.state.time.zone) {
      return;
    }
    setShownZone(zone);
    corgi.vdomCaching.disable();
    this.updateState({...this.state, time: {...this.state.time, zone}}).then(() => {
      corgi.vdomCaching.enable();
    });
  }

  private layerNodeOf(e: CorgiEvent<typeof DOM_MOUSE>): LayerNode {
    const id = checkExists(e.actionElement.data('id')).string();
    return checkExists(findLayerNode(this.state.layers.nodes, id));
  }

  private toggleLayerExpanded(id: string): void {
    const expanded = new Set(this.state.layers.expanded);
    if (!expanded.delete(id)) {
      expanded.add(id);
    }
    this.updateState({...this.state, layers: {...this.state.layers, expanded}});
  }

  private setLayerValues(entry: LayerEntry, values: ControlValues): void {
    this.updateState({
      ...this.state,
      layers: {
        ...this.state.layers,
        nodes: updateLayerNode(this.state.layers.nodes, entry.id, () => ({...entry, values})),
      },
    });
    entry.configure?.(values);
    this.layerEdits += 1;
    this.saveViewState();
  }

  private setLayerEnabled(id: string, enabled: boolean): void {
    const nodes = updateLayerNode(this.state.layers.nodes, id, node => ({...node, enabled}));
    this.updateState({
      ...this.state,
      layers: {...this.state.layers, nodes},
    });
    this.setMapLayers(nodes);
    this.layerEdits += 1;
    this.saveViewState();
  }

  private setLayerSettings(settings: LayerSettings): void {
    const before = this.state.layers.nodes;
    const nodes = applyLayerSettings(before, this.layerDefaults, settings);
    for (const {entry} of layerEntries(nodes)) {
      const previous = findLayerNode(before, entry.id) as LayerEntry;
      if (entry.values !== previous.values) {
        entry.configure?.(entry.values);
      }
    }
    this.updateState({...this.state, layers: {...this.state.layers, nodes}});
    this.setMapLayers(nodes);
  }

  private setTime(at: number|undefined, changes: Partial<TimelineState> = {}): void {
    this.catalog.setTime(at);
    this.updateTimeline({...changes, at});
  }

  private updateTimeline(changes: Partial<TimelineState>): void {
    const forecast = this.isForecast();
    this.updateState({
      ...this.state,
      time: {
        ...this.state.time,
        ...changes,
        start: this.catalog.forecastStart,
        end: this.catalog.forecastEnd,
      },
    });
    if (this.isForecast() !== forecast) {
      this.setMapLayers(this.state.layers.nodes);
    }
  }

  // Stops at the end of the forecast when it plays within it, because past it the weather would
  // disappear. A time outside of the forecast plays on for the sun and moon.
  //
  // Each tick holds until every timed layer has loaded for the current time. A forecast on the map
  // also plays a step at a time and holds each step for a few ticks more, so every step is on
  // screen long enough to see.
  private advanceTimeline(): void {
    const time = this.state.time;
    const from = time.at ?? Date.now();
    const timed = this.getShownEntries(this.state.layers.nodes).filter(e => e.timed);
    const forecasts = timed.filter(e => e.nextTime);
    if (timed.some(entry => entry.layer.loadingData())) {
      this.playTicks = 0;
      return;
    }
    const steps =
        forecasts.map(entry => entry.nextTime?.(from)).filter(step => step !== undefined);
    this.playTicks += 1;
    if (steps.length > 0 && this.playTicks % FORECAST_PLAY_TICKS !== 0) {
      return;
    }
    const at = steps.length > 0 ? Math.min(...steps) : from + STEP_MS;
    if (from <= time.end && at > time.end && time.start <= from) {
      this.player.stop();
      this.setTime(time.end, {playing: false});
      return;
    }
    this.setTime(at);
  }

  private isForecast(): boolean {
    const at = this.state.time.at ?? Date.now();
    return this.catalog.forecastStart <= at && at <= this.catalog.forecastEnd;
  }

  private getShownEntries(nodes: LayerNode[]): LayerEntry[] {
    const forecast = this.isForecast();
    return layerEntries(nodes)
        .filter(({entry, shown}) => shown && (entry.timed !== 'forecast' || forecast))
        .map(({entry}) => entry);
  }

  private setMapLayers(nodes: LayerNode[]): void {
    const entries = layerEntries(nodes);
    for (const {entry, shown} of entries) {
      if (entry.layer instanceof CollectionLayer) {
        // A hidden layer's geometry would otherwise answer hovers and catch the line tool.
        entry.layer.setIndexed(shown);
      } else if (entry.layer instanceof PointCollectionLayer) {
        entry.layer.setShown(shown);
      }
    }
    const shownEntries = this.getShownEntries(nodes);
    for (const entry of shownEntries) {
      if (!this.shownIds.has(entry.id)) {
        entry.onShown?.();
      }
    }
    this.shownIds = new Set(shownEntries.map(entry => entry.id));
    // The map draws by z, so this order only decides who hears events first. Nodes sit on
    // everything they share the map with and claim what lands on an icon.
    const shown = shownEntries.map(entry => entry.layer);
    this.mapController.setLayers(
        [
          this.pointLayer,
          this.markerLayer,
          this.lineLayer,
          this.polygonLayer,
          this.measureLayer,
          this.terrainLayer,
          this.featureLayer as Layer,
        ]
            .concat(shown.filter(layer => layer instanceof PointCollectionLayer))
            .concat(shown.filter(layer => !(layer instanceof PointCollectionLayer)))
            .concat([this.polarCapLayer, this.skyboxLayer]));
  }
}

function withoutHover(profile: LineProfileState|undefined): LineProfileState|undefined {
  return profile?.status === 'loaded' ? {...profile, hovered: undefined} : profile;
}

function getPickSchema(entry: LayerEntry): Schema|CollectionSchema|undefined {
  return entry.schema
      ?? (entry.layer instanceof CollectionLayer ? entry.layer.getCollectionSchema() : undefined);
}

// Mercator to interleaved lat then lng degrees
function toDegrees(points: Float64Array): Float64Array {
  const degrees = new Float64Array(points.length);
  for (let i = 0; i < points.length; i += 2) {
    const ll = unprojectS2LatLng(points[i], points[i + 1]);
    degrees[i] = ll.latDegrees();
    degrees[i + 1] = ll.lngDegrees();
  }
  return degrees;
}

// The point's own spot, the spot on a line or ring nearest the click, or else the click
function coordinatesOf(
    feature: EditableFeature|undefined,
    spot: LineSpot|undefined,
    latE7: number,
    lngE7: number): [number, number] {
  if (feature?.kind === 'point') {
    return [feature.latE7, feature.lngE7];
  } else if (isShape(feature) && spot?.kind === 'vertex') {
    return [feature.latLngE7[2 * spot.index], feature.latLngE7[2 * spot.index + 1]];
  } else if (spot?.kind === 'segment') {
    return [spot.latE7, spot.lngE7];
  }
  return [latE7, lngE7];
}

function stripUndefined(data: FeatureData): FeatureData {
  const stripped: FeatureData = {};
  for (const [key, value] of Object.entries(data)) {
    if (value !== undefined) {
      stripped[key] = value;
    }
  }
  return stripped;
}

function newCollectionName(): string {
  return `Untitled collection ${new Date().toLocaleDateString()}`;
}

function osmElementOf(pointId: number): {type: 'node'|'relation'|'way'; id: number} {
  const {type, id} = getPointSource(BigInt(pointId));
  return {type, id: Number(id)};
}

function isFileDrag(e: CorgiEvent<typeof DOM_MOUSE>): boolean {
  return !!(e.detail as DragEvent).dataTransfer?.types.includes('Files');
}
